// apps/landing-page/src/components/waitlist/WaitlistForm.tsx
'use client';

import { useEffect, useState, FormEvent } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

type QuestionType = 'TEXT' | 'SINGLE_SELECT' | 'MULTI_SELECT';

interface WaitlistQuestion {
  id: string;
  label: string;
  type: QuestionType;
  options: string[];
  required: boolean;
  sortOrder: number;
}

type AnswerValue = string | string[];

interface WaitlistFormProps {
  feature: string;
  source: string;
  /** Human product name, e.g. "Kursus Mandiri" or "Live Class" — interpolated into confirmationDesc. */
  product: string;
}

// Mirrors WAITLIST_VALIDATION_ERRORS in apps/api/src/modules/waitlist/domain/waitlist.types.ts
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  MISSING_REQUIRED: 'errorMissingRequired',
  INVALID_OPTION: 'errorInvalidOption',
  INVALID_ANSWER_TYPE: 'errorInvalidAnswerType',
  UNKNOWN_QUESTION: 'errorUnknownQuestion',
};

export function WaitlistForm({ feature, source, product }: WaitlistFormProps) {
  const t = useTranslations('waitlistForm');
  const [questions, setQuestions] = useState<WaitlistQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/proxy/waitlist/questions?feature=${encodeURIComponent(feature)}`)
      .then((r) => (r.ok ? r.json() : { questions: [] }))
      .then((data) => {
        if (!cancelled) setQuestions(Array.isArray(data.questions) ? data.questions : []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setQuestionsLoading(false); });
    return () => { cancelled = true; };
  }, [feature]);

  function setTextAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  function toggleMultiAnswer(questionId: string, option: string) {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
      const next = current.includes(option)
        ? current.filter((v) => v !== option)
        : [...current, option];
      return { ...prev, [questionId]: next };
    });
  }

  const sortedQuestions = questions.slice().sort((a, b) => a.sortOrder - b.sortOrder);

  function findUnansweredRequiredQuestion(): WaitlistQuestion | null {
    for (const q of sortedQuestions) {
      if (!q.required) continue;
      if (q.type === 'SINGLE_SELECT') {
        if (typeof answers[q.id] !== 'string' || !answers[q.id]) return q;
      } else if (q.type === 'MULTI_SELECT') {
        const val = answers[q.id];
        if (!Array.isArray(val) || val.length === 0) return q;
      }
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const unanswered = findUnansweredRequiredQuestion();
    if (unanswered) {
      setError(t('validationRequiredQuestion', { label: unanswered.label }));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/proxy/auth/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, feature, source, answers }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const messageKey = ERROR_MESSAGE_KEYS[data.error];
        throw new Error(messageKey ? t(messageKey) : data.error || t('genericError'));
      }
      if (data.alreadyRegistered) setAlreadyRegistered(true);
      else setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('genericError'));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted || alreadyRegistered) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <CheckCircle className="w-9 h-9 text-teal-500" />
        <p className="text-slate-800 font-semibold">
          {alreadyRegistered ? t('alreadyRegistered') : t('submitted')}
        </p>
        <p className="text-slate-500 text-sm">{t('confirmationDesc', { email, product })}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">{t('nameLabel')}</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('namePlaceholder')}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30 transition-colors"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">{t('emailLabel')}</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('emailPlaceholder')}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30 transition-colors"
        />
      </div>

      {!questionsLoading && sortedQuestions.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          {sortedQuestions.map((q) => (
            <div key={q.id}>
              <label id={`waitlist-q-label-${q.id}`} className="block text-xs font-semibold text-slate-600 mb-1.5">
                {q.label}
                {q.required && <span className="text-red-500"> *</span>}
              </label>
              {q.type === 'TEXT' && (
                <input
                  type="text"
                  required={q.required}
                  value={(answers[q.id] as string) ?? ''}
                  onChange={(e) => setTextAnswer(q.id, e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-sm text-slate-800 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30 transition-colors"
                />
              )}
              {q.type === 'SINGLE_SELECT' && (
                <div role="group" aria-labelledby={`waitlist-q-label-${q.id}`} className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      aria-pressed={answers[q.id] === opt}
                      onClick={() => setTextAnswer(q.id, opt)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        answers[q.id] === opt
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {q.type === 'MULTI_SELECT' && (
                <div role="group" aria-labelledby={`waitlist-q-label-${q.id}`} className="flex flex-wrap gap-1.5">
                  {q.options.map((opt) => {
                    const selected = Array.isArray(answers[q.id]) && (answers[q.id] as string[]).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => toggleMultiAnswer(q.id, opt)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('submitBtn')}
      </button>
    </form>
  );
}
